import React, { useEffect, useState } from 'react';
import { generateQRCode, downloadQRCode, QRCodeData } from '../../lib/qr-code';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { cn, formatPhoneNumber } from '../../lib/utils';

interface QRCodeDisplayProps {
  data: QRCodeData;
  size?: number;
  showDownloadButton?: boolean;
  showInfo?: boolean;
  className?: string;
}

/**
 * QR 코드 표시 컴포넌트
 * 등록 정보를 바탕으로 QR 코드를 생성하고 표시합니다.
 */
export const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  data,
  size = 256,
  showDownloadButton = true,
  showInfo = true,
  className
}) => {
  const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // QR 코드 생성
  useEffect(() => {
    const generateQR = async () => {
      try {
        setIsLoading(true);
        setError('');
        
        const qrCode = await generateQRCode(data);
        setQrCodeDataURL(qrCode);
      } catch (err) {
        setError('QR 코드 생성에 실패했습니다.');
        console.error('QR 코드 생성 에러:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (data.id) {
      generateQR();
    }
  }, [data]);

  // QR 코드 다운로드 핸들러
  const handleDownload = async () => {
    try {
      const filename = `exhibition-qr-${data.name}-${data.id}.png`;
      await downloadQRCode(data, filename);
    } catch (err) {
      alert('QR 코드 다운로드에 실패했습니다.');
      console.error('QR 코드 다운로드 에러:', err);
    }
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <Card className={cn('w-full max-w-sm mx-auto', className)}>
        <CardContent className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">QR 코드 생성 중...</span>
        </CardContent>
      </Card>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <Card className={cn('w-full max-w-sm mx-auto', className)}>
        <CardContent className="flex items-center justify-center p-8">
          <div className="text-red-500 text-center">
            <p className="font-semibold">오류 발생</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('w-full max-w-sm mx-auto', className)}>
      {showInfo && (
        <CardHeader className="text-center">
          <CardTitle className="text-lg">입장용 QR 코드</CardTitle>
          <p className="text-sm text-gray-600">
            전시회 입장 시 아래 QR 코드를 제시해주세요.
          </p>
        </CardHeader>
      )}
      
      <CardContent className="flex flex-col items-center space-y-4">
        {/* QR 코드 이미지 */}
        <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
          <img
            src={qrCodeDataURL}
            alt="전시회 입장용 QR 코드"
            style={{ width: size, height: size }}
            className="block"
          />
        </div>

        {/* 등록 정보 */}
        {showInfo && (
          <div className="text-center space-y-1">
            <p className="font-semibold text-gray-800">{data.name}</p>
            <p className="text-sm text-gray-600">{formatPhoneNumber(data.phone)}</p>
            <p className="text-xs text-gray-500">
              등록일: {new Date(data.created_at).toLocaleDateString('ko-KR')}
            </p>
          </div>
        )}

        {/* 다운로드 버튼 */}
        {showDownloadButton && (
          <Button
            onClick={handleDownload}
            variant="outline"
            className="w-full"
          >
            QR 코드 다운로드
          </Button>
        )}

        {/* 안내 메시지 */}
        <div className="text-center text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
          <p>• 이 QR 코드는 전시회 입장 시에만 사용됩니다.</p>
          <p>• 스크린샷을 저장하거나 인쇄하여 지참해주세요.</p>
          <p>• 문의사항이 있으시면 전시회 운영진에게 연락하세요.</p>
        </div>
      </CardContent>
    </Card>
  );
};

// 간단한 QR 코드 프리뷰 컴포넌트
export const QRCodePreview: React.FC<{
  data: QRCodeData;
  size?: number;
  className?: string;
}> = ({ data, size = 128, className }) => {
  const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const generateQR = async () => {
      try {
        const qrCode = await generateQRCode(data);
        setQrCodeDataURL(qrCode);
      } catch (err) {
        console.error('QR 코드 생성 에러:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (data.id && data.registration_id) {
      generateQR();
    }
  }, [data]);

  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center', className)}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!qrCodeDataURL) {
    return (
      <div className={cn('flex items-center justify-center bg-gray-100 rounded', className)}>
        <span className="text-gray-500 text-sm">QR 코드 생성 실패</span>
      </div>
    );
  }

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <img
        src={qrCodeDataURL}
        alt="QR 코드 미리보기"
        style={{ width: size, height: size }}
        className="rounded border"
      />
    </div>
  );
}; 